import React, { useEffect, useRef, useState } from 'react';
import { getdata, askchiefofstaff, getchiefofstaffstatus } from '../../util/global';

const DEFAULT_CHIPS = [
  'What did we bill this month?',
  'Top outstanding invoices?',
  'Expenses this month vs last?',
  'Who owes us money?',
  'Cash summary',
  'Outstanding payables?',
];

interface Message {
  _id?:      string;
  question:  string;
  answer:    string;
  source:    string;
}

const ChiefDashboard = () => {
  const [messages,  setMessages]  = useState<Message[]>([]);
  const [chips,     setChips]     = useState<string[]>(DEFAULT_CHIPS);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [zohoLive,  setZohoLive]  = useState(false);

  const bottomRef   = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () =>
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });

  const fetchHistory = async () => {
    try {
      const data = await getdata({ filter: { datatype: 'message' } });
      if (Array.isArray(data)) setMessages(data.slice(-50));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchChips = async () => {
    try {
      const data = await getdata({ filter: { datatype: 'questionlibrary' } });
      if (Array.isArray(data) && data.length > 0) {
        const loaded = data.map((d: any) => d.question).filter(Boolean);
        if (loaded.length > 0) setChips(loaded);
      }
    } catch (err) {
      // keep DEFAULT_CHIPS
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchChips();
    getchiefofstaffstatus().then((s: any) => setZohoLive(s?.zoho === 'connected'));
  }, []);
  useEffect(() => { scrollToBottom(); }, [messages, loading]);

  const sendQuestion = async (question: string) => {
    if (!question.trim() || loading) return;
    setInput('');
    setLoading(true);

    // Optimistic entry — answer empty while we wait
    setMessages(prev => [...prev, { question, answer: '', source: '' }]);

    try {
      const result = await askchiefofstaff(question.trim());
      if (result) {
        setMessages(prev => [
          ...prev.slice(0, -1),
          { question, answer: result.answer, source: result.source },
        ]);
      }
    } catch {
      setMessages(prev => [
        ...prev.slice(0, -1),
        { question, answer: 'Something went wrong. Please try again.', source: '' },
      ]);
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion(input);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  return (
    <>
      {/* ── Message history ── */}
      <div className="chief-chat-area">
        {messages.length === 0 && !loading && (
          <div className="chief-chat-empty">
            <span>Ask anything about your business.</span>
            <span style={{ fontSize: '0.78rem' }}>
              {zohoLive
                ? 'Connected to your Zoho Books · Meta Ads & Shopify on demo'
                : 'Using Lumio demo data · connect live sources in Settings'}
            </span>
          </div>
        )}

        {messages.map((msg, i) => {
          const isLast   = i === messages.length - 1;
          const thinking = isLast && loading && !msg.answer;
          return (
            <React.Fragment key={i}>
              <div className="chief-msg user">
                <div className="chief-msg-bubble">{msg.question}</div>
              </div>

              {thinking ? (
                <div className="chief-msg ai">
                  <div className="chief-thinking">
                    <span /><span /><span />
                  </div>
                </div>
              ) : msg.answer ? (
                <div className="chief-msg ai">
                  <div className="chief-msg-bubble">{msg.answer}</div>
                  {msg.source && (
                    <div className="chief-msg-source">{msg.source}</div>
                  )}
                </div>
              ) : null}
            </React.Fragment>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* ── Suggested chips (hide once conversation is underway) ── */}
      {messages.length < 2 && (
        <div className="chief-chips">
          {chips.map((chip, i) => (
            <button
              key={i}
              className="chief-chip"
              onClick={() => sendQuestion(chip)}
              disabled={loading}
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      {/* ── Input bar ── */}
      <div className="chief-input-bar">
        <div className="chief-input-row">
          <textarea
            ref={textareaRef}
            className="chief-input-textarea"
            value={input}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about your business…"
            rows={1}
            disabled={loading}
          />
          <button
            className="chief-send-btn"
            onClick={() => sendQuestion(input)}
            disabled={!input.trim() || loading}
            aria-label="Send"
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
};

export default ChiefDashboard;
